import React, { useCallback, useEffect, useRef } from "react";
import { FormHelperText, Typography } from "@mui/material";
import { DocumentUpload } from "@components/FileUpload/FileUpload";
import { useFileUploadProfiles } from "@components/FileUpload/FileUpload.constants";
import SingleFile from "@components/FileUpload/SingleFile";
import { useUploadStore, type UploadEntry } from "@components/FileUpload/uploadStore";
import { usePortServicesFlow } from "@features/vessel-call/vessel-call-status/PortServices/flow/context/PortServicesFlowContext";
import { uploadFileToAzureBlob } from "@services/azureDataApi";
import { SasTokenPrefix, SasTokenPrivilege } from "@services/user/types";
import { getSasToken } from "@services/userApi";
import { useMutation } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

const LANG_BASE = "vessel-call.port-services";

const ServiceReportAttachment = () => {
  const EMPTY_UPLOAD_ENTRIES: UploadEntry[] = [];
  const { t } = useTranslation();
  const { errors, setErrors, setServiceReportAttachment } = usePortServicesFlow();

  const serviceReportAttachmentUploadSignatureRef = useRef<string | null>(null);
  const setFilesForKey = useUploadStore((s) => s.setFilesForKey);
  const clearFilesForKey = useUploadStore((s) => s.clearFilesForKey);

  const serviceReportAttachment = useUploadStore(
    (s) => s.filesByKey.serviceReportAttachment ?? EMPTY_UPLOAD_ENTRIES,
  );

  const fileProfiles = useFileUploadProfiles();
  const internalProfile = fileProfiles.portServicesRequestFile;
  const allowedInternalExtensions = internalProfile.format.map((ext) =>
    ext.startsWith(".") ? ext.toLowerCase() : `.${ext.toLowerCase()}`,
  );

  const setServiceReportAttachmentFiles = useCallback(
    (files: UploadEntry[]) => {
      if (!files.length) {
        clearFilesForKey("serviceReportAttachment");
        return;
      }
      setFilesForKey("serviceReportAttachment", files);
    },
    [clearFilesForKey, setFilesForKey],
  );

  const getFileSignature = (entry: UploadEntry | undefined) => {
    if (!(entry instanceof File)) return null;
    return `${entry.name}-${entry.lastModified}-${entry.size}`;
  };

  const triggerUpload = useCallback(
    (
      entry: UploadEntry | undefined,
      handler: ((file: File) => void) | undefined,
      tracker: React.MutableRefObject<string | null>,
    ) => {
      if (!handler) return;
      if (!(entry instanceof File)) return;

      const signature = getFileSignature(entry);
      if (!signature) return;

      if (tracker.current === signature) return;

      tracker.current = signature;
      handler(entry);
    },
    [],
  );

  const removeServiceReportAttachment = () => {
    setErrors((prev) => ({ ...prev, serviceReportAttachment: undefined, files: undefined }));
    clearFilesForKey("serviceReportAttachment");
    setServiceReportAttachment({ documentUrl: "", documentName: "", fileType: "" });
  };

  const documentUploadMutation = useMutation({
    mutationFn: ({ file, documentType }: { file: File; documentType: string }) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "PortServiceDocumentUpload",
          action: "upload_port_service_document",
          entityType: "port_service_document",
        },
        async (transactionId) => {
          const payloadForSasToken = {
            prefix: SasTokenPrefix.PORT_SERVICES,
            contentType: file.type || null,
            privilege: SasTokenPrivilege.WRITE,
            blobUrl: null,
          };

          const { blobUrl, sasToken } = await getSasToken(
            payloadForSasToken,
            ...(transactionId ? [transactionId] : []),
          );
          const [uploadResult] = await uploadFileToAzureBlob({
            file,
            blobUrl,
            sasToken,
            certificateType: documentType,
          });

          const documentUrl = uploadResult?.documentUrl ?? blobUrl;
          setServiceReportAttachment({
            documentUrl: documentUrl,
            documentName: file?.name,
            fileType: file?.type,
          });
          return { documentUrl, documentType };
        },
      ),
  });

  const handleDocumentUpload = (file: File, documentType: string) => {
    documentUploadMutation.mutate({ file, documentType });
  };

  const handleFileUpload = ({
    newFiles,
    setUploadErrors,
    setUploadFiles,
    onUploadDocument,
    uploadTracker,
  }: {
    newFiles: File[];
    setUploadErrors: (value: { serviceReportAttachment?: string; files?: string }) => void;
    setUploadFiles: (files: UploadEntry[]) => void;
    onUploadDocument?: (file: File) => void;
    uploadTracker: React.MutableRefObject<string | null>;
  }) => {
    if (newFiles.length > 1) {
      setUploadErrors({ serviceReportAttachment: t("upload.one-document") });
      return;
    }

    const file = newFiles[0];
    if (!file) {
      return;
    }

    setUploadErrors({ serviceReportAttachment: undefined, files: undefined });
    setUploadFiles([file]);
    triggerUpload(file, onUploadDocument, uploadTracker);
  };

  useEffect(() => {
    return () => {
      serviceReportAttachmentUploadSignatureRef.current = null;
      removeServiceReportAttachment();
    };
  }, []);

  return (
    <div className="flex flex-col gap-2 mb-4" data-testid="port-services-service-report">
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {t(`${LANG_BASE}.fields.service-report`)}
      </Typography>
      <DocumentUpload
        testId="port-services-service-report-upload"
        onUpload={(newFiles) =>
          handleFileUpload({
            newFiles,
            setUploadErrors: (fileErrors) =>
              setErrors({
                ...errors,
                ...fileErrors,
              }),
            setUploadFiles: setServiceReportAttachmentFiles,
            onUploadDocument: (file) => handleDocumentUpload(file, "SERVICE_REPORT_DOCUMENT"),
            uploadTracker: serviceReportAttachmentUploadSignatureRef,
          })
        }
        name="serviceReportAttachment"
        conditions={internalProfile}
        files={serviceReportAttachment}
        setFiles={(next) => setFilesForKey("serviceReportAttachment", next)}
        maxFiles={1}
        allowedExtensions={allowedInternalExtensions}
        uploadDescription={t("upload.additional-documents-upload", {
          extensions: allowedInternalExtensions.join(", "),
          size: internalProfile.maxSizeLabel,
        })}
      />
      {errors.serviceReportAttachment && (
        <FormHelperText sx={{ color: "error.main", m: 0 }}>
          {errors.serviceReportAttachment}
        </FormHelperText>
      )}
      {serviceReportAttachment[0] && (
        <SingleFile
          file={serviceReportAttachment[0]}
          testId="port-services-service-report"
          fieldName="serviceReportAttachment"
          currentFileIndex={0}
          setFiles={() => {
            setErrors({
              ...errors,
              serviceReportAttachment: undefined,
              files: undefined,
            });
            setServiceReportAttachmentFiles([]);
            serviceReportAttachmentUploadSignatureRef.current = null;
          }}
          removeFn={removeServiceReportAttachment}
        />
      )}
    </div>
  );
};

export default ServiceReportAttachment;
